import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';

export type InspectionBreadcrumbMode = 'view' | 'create' | 'edit';

export interface UseInspectionBreadcrumbsProps {
  mode: InspectionBreadcrumbMode;
  /** Site/type name shown in the `view`/`edit` tail. */
  name?: string;
  inspectionId?: string | number;
}

export interface UseInspectionBreadcrumbsResult {
  breadcrumbs: BreadcrumbItem[];
  fallbackTo: string;
  fallbackLabel: string;
}

const INSPECTIONS_ROOT_LABEL = 'Inspections';

export const useInspectionBreadcrumbs = ({
  mode,
  name,
  inspectionId,
}: UseInspectionBreadcrumbsProps): UseInspectionBreadcrumbsResult => {
  return useMemo((): UseInspectionBreadcrumbsResult => {
    const rootPath = PathUtility.Inspections('list');
    const root: BreadcrumbItem = {label: INSPECTIONS_ROOT_LABEL, to: rootPath};
    const rootFallback = {
      fallbackTo: rootPath,
      fallbackLabel: `Back to ${INSPECTIONS_ROOT_LABEL}`,
    };

    switch (mode) {
      case 'create':
        return {
          breadcrumbs: [root, {label: 'New Inspection'}],
          ...rootFallback,
        };

      case 'edit': {
        const viewLabel = name || 'Inspection';
        const viewPath = PathUtility.InspectionsView(inspectionId ?? '');
        return {
          breadcrumbs: [root, {label: viewLabel, to: viewPath}, {label: 'Edit'}],
          fallbackTo: viewPath,
          fallbackLabel: `Back to ${viewLabel}`,
        };
      }

      case 'view':
      default:
        return {
          breadcrumbs: [root, {label: name || 'Inspection'}],
          ...rootFallback,
        };
    }
  }, [mode, name, inspectionId]);
};
